import {
	CoachListData,
	CoachSlotGroupItem,
	CouchGroupByVehicleEnum,
	SlotVehicle,
	VehicleLayout,
	CoachVehicle,
} from '@/new-transport/_models/transport-coach-list.model';
import { TransportSide } from '@/new-transport/_models/transport.model';

/**
 * Updates the coachList state with a new seat assignment for a specific vehicle in a slot.
 *
 * @param {CoachListData[]} coachList - Current list of coaches from the state.
 * @param {CoachSlotGroupItem} selectedSlot - The coach slot that contains the vehicle to be updated.
 * @param {SlotVehicle} slotVehicleToUpdate - The slot vehicle to be updated.
 * @param {VehicleLayout} layout - The layout object containing seat information.
 * @param {number} seatId - The ID of the new seat to assign.
 * @param {CoachVehicle} newVehicle - The new vehicle to assign to the slot vehicle.
 * @param {TransportSide} transportSide - The side of transport. Can be departure or return.
 * @param {CouchGroupByVehicleEnum} vehicleType - The type of the vehicle group ('before', 'after' or 'single')
 *   the seat was chosen in. Used to determine the {@link SlotVehicle.order} of a newly created slot vehicle.
 * @returns {CoachListData[]} Updated coach list with the assigned seat.
 */
export function updateCoachListWithSeat(
	coachList: CoachListData[],
	selectedSlot: CoachSlotGroupItem,
	slotVehicleToUpdate: SlotVehicle,
	layout: VehicleLayout,
	seatId: number,
	newVehicle: CoachVehicle,
	transportSide: TransportSide,
	vehicleType: CouchGroupByVehicleEnum,
): CoachListData[] {
	const selectedSeat = layout.seats.find((seat) => seat.id === seatId);
	if (!selectedSeat) {
		return coachList;
	}

	return coachList.map((coach) => {
		if (coach.id !== selectedSlot.id) {
			return coach;
		}

		if (slotVehicleToUpdate === null) {
			const order = vehicleType === CouchGroupByVehicleEnum.AFTER ? 1 : 0;
			const existingSlotVehicle = coach.slotVehicles.find((v) => v.side === transportSide && v.order === order);

			if (existingSlotVehicle) {
				return {
					...coach,
					slotVehicles: coach.slotVehicles.map((v) =>
						v === existingSlotVehicle ? { ...v, seat: selectedSeat, vehicle: newVehicle } : v,
					),
				};
			}

			return {
				...coach,
				slotVehicles: [
					...coach.slotVehicles,
					{
						id: null,
						order,
						seat: selectedSeat,
						side: transportSide,
						vehicle: newVehicle,
					},
				],
			};
		}

		const updatedSlotVehicles = coach.slotVehicles.map((vehicle) => {
			if (vehicle.side === slotVehicleToUpdate.side && vehicle.vehicle.id === slotVehicleToUpdate.vehicle.id) {
				return {
					...vehicle,
					seat: selectedSeat,
					vehicle: newVehicle,
				};
			} else {
				return vehicle;
			}
		});

		return {
			...coach,
			slotVehicles: updatedSlotVehicles,
		};
	});
}
